import { useCallback, useMemo } from "react"
import { useMutation, useQuery } from "urql"

import type {
  CompanyPaymentType,
  CompanyPaymentTypeMethod,
  CreateCompanyPaymentTypeMutationVariables,
  Mutation,
  Query,
  UpdateCompanyPaymentTypeMethodMutationVariables,
  UpdateCompanyPaymentTypeMutationVariables,
} from "@/graphql/codegen/graphql"
import {
  CompanyStatus,
  CreateCompanyPaymentTypeDocument,
  GetCompanyPaymentTypesDocument,
  PaymentMethod,
  UpdateCompanyPaymentTypeDocument,
  UpdateCompanyPaymentTypeMethodDocument,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCompanyPaymentTypesProps {
  companyId?: string
  pause?: boolean
}

interface UseCompanyPaymentTypesReturnProps {
  paymentTypes: CompanyPaymentType[]
  paymentMethods: CompanyPaymentTypeMethod[]
  loading: boolean
  refetchPaymentTypes: () => void
  createPaymentType: (
    variables: CreateCompanyPaymentTypeMutationVariables
  ) => Promise<{ data?: Pick<Mutation, "companyPaymentType"> | null; error?: unknown }>
  updatePaymentType: (
    variables: UpdateCompanyPaymentTypeMutationVariables
  ) => Promise<{ data?: Pick<Mutation, "companyPaymentType"> | null; error?: unknown }>
  updatePaymentTypeMethod: (
    variables: UpdateCompanyPaymentTypeMethodMutationVariables
  ) => Promise<{ data?: Pick<Mutation, "companyPaymentType"> | null; error?: unknown }>
  deletePaymentType: (companyPaymentTypeId: string) => Promise<void>
  deletePaymentTypeMethod: (companyPaymentMethodId: string) => Promise<void>
  togglePaymentMethodActivation: (companyPaymentMethodId: string, currentStatus: CompanyStatus) => Promise<void>
}

export const useCompanyPaymentTypes = ({
  companyId = "",
  pause = false,
}: UseCompanyPaymentTypesProps = {}): UseCompanyPaymentTypesReturnProps => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ data, fetching }, executeQuery] = useQuery<Pick<Query, "companyPaymentType">>({
    query: GetCompanyPaymentTypesDocument,
    variables: { input: { companyId } },
    pause: pause || !companyId,
  })

  const refetchPaymentTypes = useCallback(() => {
    executeQuery({ requestPolicy: "network-only" })
  }, [executeQuery])

  const paymentTypes = useMemo(() => {
    if (!data?.companyPaymentType?.list) {
      return []
    }
    return data.companyPaymentType.list.__typename === "CompanyPaymentTypeListSuccess"
      ? (data.companyPaymentType.list.companyPaymentType ?? [])
      : []
  }, [data]) as CompanyPaymentType[]

  const paymentMethods = useMemo(() => {
    return paymentTypes.flatMap((paymentType) => (paymentType.paymentMethods ?? []).filter(Boolean))
  }, [paymentTypes]) as CompanyPaymentTypeMethod[]

  const [, createPaymentType] = useMutation<
    Pick<Mutation, "companyPaymentType">,
    CreateCompanyPaymentTypeMutationVariables
  >(CreateCompanyPaymentTypeDocument)

  const [, updatePaymentType] = useMutation<
    Pick<Mutation, "companyPaymentType">,
    UpdateCompanyPaymentTypeMutationVariables
  >(UpdateCompanyPaymentTypeDocument)

  const [, updatePaymentTypeMethod] = useMutation<
    Pick<Mutation, "companyPaymentType">,
    UpdateCompanyPaymentTypeMethodMutationVariables
  >(UpdateCompanyPaymentTypeMethodDocument)

  const deletePaymentType = useCallback(
    async (companyPaymentTypeId: string) => {
      const { error } = await updatePaymentType({
        input: {
          companyPaymentTypeId,
          isDeleted: true,
        },
      })

      if (error) {
        console.error(error)
      }

      refetchPaymentTypes()
    },
    [updatePaymentType, refetchPaymentTypes]
  )

  const deletePaymentTypeMethod = useCallback(
    async (companyPaymentMethodId: string) => {
      await updatePaymentTypeMethod({
        input: { paymentMethodId: companyPaymentMethodId, isDeleted: true },
      })

      refetchPaymentTypes()
    },
    [updatePaymentTypeMethod, refetchPaymentTypes]
  )

  const togglePaymentMethodActivation = useCallback(
    async (companyPaymentMethodId: string, currentStatus: CompanyStatus) => {
      const currentPaymentMethod = paymentMethods.find(
        (method) => method.companyPaymentMethodId === companyPaymentMethodId
      )

      if (!currentPaymentMethod) {
        return
      }

      let nextStatus

      if (currentStatus !== CompanyStatus.Inactive) {
        nextStatus = CompanyStatus.Inactive
      } else if (
        currentPaymentMethod.method === PaymentMethod.Ach ||
        currentPaymentMethod.method === PaymentMethod.Wire
      ) {
        nextStatus = CompanyStatus.Pending
      } else {
        nextStatus = CompanyStatus.Active
      }

      const { error } = await updatePaymentTypeMethod({
        input: {
          paymentMethodId: companyPaymentMethodId,
          status: nextStatus,
        },
      })

      if (error) {
        notify({
          message: "Failed to update payment method",
          type: NotificationType.Error,
        })
        console.error(error)
        return
      } else {
        notify({
          message: `Payment method has been successfully ${nextStatus === CompanyStatus.Inactive ? "deactivated" : "activated"}`,
          type: NotificationType.Success,
        })
      }

      refetchPaymentTypes()
    },
    [updatePaymentTypeMethod, refetchPaymentTypes, paymentMethods, notify]
  )

  return {
    paymentTypes,
    paymentMethods,
    loading: fetching,
    refetchPaymentTypes,
    createPaymentType,
    updatePaymentType,
    updatePaymentTypeMethod,
    deletePaymentType,
    deletePaymentTypeMethod,
    togglePaymentMethodActivation,
  }
}

export default useCompanyPaymentTypes
